import React, { useCallback, useEffect, useRef, useState } from "react";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { CreateCommanWay, GetCommanWay, ListCommanWay, UpdateCommanWay, PostFormData } from "API/commonApi";
import { AfDataType as ADT } from "utils/TableColumnData";
import { useFormik } from "formik";
import { AxiosResponse } from "axios";
import UseToast from "utils/AfToast";
import { debounce } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import moment from "moment";
import Spinner from "utils/Spinner";

export type AfTableProps = Partial<{
  className: string;
  columnData: any[];
  selectable: boolean;
  doAdd: boolean;
  doEdit: boolean;
  doDelete: boolean;
  doExport: boolean;
  paginate: boolean;
  addBtnUrl: string;
  validationSchema: any;
  initialValues: any;
  apiUrl: string;
  successNav: string;
  cancelNav: string;
  mode: string;
}>;

const GeneralAddForm: React.FC<AfTableProps> = ({
  columnData = [],
  doAdd = true,
  doEdit = true,
  doDelete = true,
  doExport = true,
  paginate = true,
  validationSchema,
  initialValues = {},
  apiUrl = "",
  successNav = "/",
  cancelNav = "/",
  mode = "View",
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const [dataMode, setDataMode] = useState<string>(mode);
  type OptionType = {
    value: number;
    label: string;
  };
  const defaultOptions: any = [{ value: 0, label: "Loading..." }];
  const [nestedTables, setNestedTables] = useState<any>({});
  const [nestedSelectedOptions, setNestedSelectedOptions] = useState<any>({});
  const [selectedFiles, setSelectedFiles] = useState<any>({});
  const [existingFileUrls, setExistingFileUrls] = useState<any>({});
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  
  // Special state for vendor payment multi-file upload
  const [vendorInvoiceFile, setVendorInvoiceFile] = useState<File | null>(null);
  const [vendorAirwaybillFiles, setVendorAirwaybillFiles] = useState<File[]>([]);
  const [existingVendorInvoice, setExistingVendorInvoice] = useState<string>("");
  const [existingVendorAirwaybills, setExistingVendorAirwaybills] = useState<string[]>([]);
  const [vendorFilesModified, setVendorFilesModified] = useState<boolean>(false); // Track if files were modified
  const [invoiceAiLoading, setInvoiceAiLoading] = useState<boolean>(false);

  // Handle Exa Check - AI Invoice Extraction
  const handleExaCheck = async () => {
    if (!vendorInvoiceFile) {
      alert("Please select an invoice file first");
      return;
    }
    if (invoiceAiLoading) return;
    
    setInvoiceAiLoading(true);
    try {
      const formData = new FormData();
      formData.append("files", vendorInvoiceFile);
      
      const response = await PostFormData("/admin/ai/invoice-extraction", formData);
      console.log("AI Invoice Extraction Response:", response);
      
      if (response.status === "SUCCESS" && response.data?.[0]?.fields?.[0]) {
        const fields = response.data[0].fields[0];
        console.log("Extracted Fields:", fields);
        
        // Invoice Date
        if (fields.InvoiceDate?.valueDate) {
          setFieldValue("invoiceDate", fields.InvoiceDate.valueDate);
        }
        
        // From/To Date from ServiceStartDate/ServiceEndDate
        if (fields.ServiceStartDate?.valueDate) {
          setFieldValue("fromDate", fields.ServiceStartDate.valueDate);
        }
        if (fields.ServiceEndDate?.valueDate) {
          setFieldValue("toDate", fields.ServiceEndDate.valueDate);
        }
        
        // Bill No (Invoice ID)
        if (fields.InvoiceId?.valueString) {
          setFieldValue("billNo", fields.InvoiceId.valueString);
        }
        
        // Basic Amount (SubTotal)
        let extractedBasicAmount = 0;
        if (fields.SubTotal?.valueCurrency?.amount) {
          extractedBasicAmount = Math.round(fields.SubTotal.valueCurrency.amount);
          setFieldValue("basicAmount", extractedBasicAmount);
        }
        
        // Tax Details - determine CGST/SGST or IGST
        let totalTax = 0;
        if (fields.TaxDetails?.valueArray?.length > 0) {
          let cgstAmount = 0;
          let sgstAmount = 0;
          let igstAmount = 0;
          
          fields.TaxDetails.valueArray.forEach((tax: any) => {
            const content = tax.content?.toLowerCase() || "";
            const amount = tax.valueObject?.Amount?.valueCurrency?.amount || 0;
            
            if (content.includes("cgst")) {
              cgstAmount += amount;
            } else if (content.includes("sgst")) {
              sgstAmount += amount;
            } else if (content.includes("igst")) {
              igstAmount += amount;
            } else {
              // Default: If just "GST" with 18%, treat as IGST
              igstAmount += amount;
            }
          });
          
          cgstAmount = Math.round(cgstAmount);
          sgstAmount = Math.round(sgstAmount);
          igstAmount = Math.round(igstAmount);
          totalTax = cgstAmount + sgstAmount + igstAmount;
          setFieldValue("cgst", cgstAmount);
          setFieldValue("sgst", sgstAmount);
          setFieldValue("igst", igstAmount);
        }
        
        // Final Amount (Invoice Total)
        let extractedFinalAmount = 0;
        if (fields.InvoiceTotal?.valueCurrency?.amount) {
          extractedFinalAmount = Math.round(fields.InvoiceTotal.valueCurrency.amount);
          setFieldValue("finalAmount", extractedFinalAmount);
        }
        
        // Cross-verify: basicAmount + tax = finalAmount (±1 tolerance)
        if (extractedBasicAmount > 0 && extractedFinalAmount > 0) {
          const calculatedTotal = extractedBasicAmount + totalTax;
          const diff = Math.abs(calculatedTotal - extractedFinalAmount);
          if (diff > 1) {
            UseToast(`Warning: Amount mismatch! Basic (${extractedBasicAmount}) + Tax (${totalTax}) = ${calculatedTotal}, but Final Amount is ${extractedFinalAmount}. Difference: ${diff.toFixed(2)}`, "warning");
          }
        }
        
        // Calculate TDS based on selected vendor's TDS percentage
        const selectedVendorId = values.vendor || nestedSelectedOptions?.vendor?.value;
        if (selectedVendorId && extractedBasicAmount > 0) {
          try {
            const vendorResponse = await ListCommanWay("/admin/vendor/list", { id: selectedVendorId }, {});
            if (vendorResponse.status === "SUCCESS") {
              const vendorData = vendorResponse.data?.data?.[0] || vendorResponse.data?.rows?.[0];
              const vendorTdsPercent = vendorData?.tds || 0;
              if (vendorTdsPercent > 0) {
                const tdsAmount = Math.round((extractedBasicAmount * vendorTdsPercent) / 100);
                setFieldValue("tds", tdsAmount);
                console.log(`TDS calculated: ${extractedBasicAmount} * ${vendorTdsPercent}% = ${tdsAmount}`);
              }
            }
          } catch (err) {
            console.error("Failed to fetch vendor TDS:", err);
          }
        }
        
        UseToast("Invoice data extracted successfully", "success");
      } else {
        alert("Failed to extract invoice data");
      }
    } catch (error) {
      console.error("Invoice extraction error:", error);
      alert("Error extracting invoice data");
    } finally {
      setInvoiceAiLoading(false);
    }
  };

  useEffect(() => {
    if (dataMode == "View") {
      const id: string | undefined = params?.id;
      console.log(id);

      let includes_ = [];
      columnData.forEach((cd) => {
        if (cd.type == ADT.DB) {
          includes_.push("_" + cd.key);
        }
      });

      ListCommanWay(apiUrl + "/list", { id: id }, { include: includes_ }).then((x) => {
        if (x.status == "SUCCESS") {
          x.data = x.data?.data ? x.data?.data[0] : x.data?.rows[0];
          console.log(x.data);
          let vals = {};
          let n = { ...nestedSelectedOptions };
          columnData.forEach((cd) => {
            vals[cd.key] = x.data[cd.key];
            //setValues(vals);
            //console.log(cd.key, x.data[cd.key], cd.type, ADT.DB);

            if (cd.type == ADT.SELECT) {
              let opt = { value: x.data[cd.key], label: x.data[cd.key] };
              console.log(opt);
              n[cd.key] = opt;
              setFieldValue(cd.key, opt);
            } else if (cd.type == ADT.BOOLEAN) {
              let opt = { value: x.data[cd.key] || false, label: x.data[cd.key] ? "Yes" : "No" };
              n[cd.key] = opt;
              console.log(cd.key, opt);
              setFieldValue(cd.key, opt);
            } else if (cd.type == ADT.DB) {
              //console.log(x.data);
              //n[cd.db] = { value: x.data[cd.key], label: x.data[cd.dd_label] };
              //setFieldValue(cd.key, { value: x.data[cd.key], label: x.data[cd.dd_label] });
              try {
                let opt = { value: x.data[cd.key], label: x.data["_" + cd.key][cd.dd_label] };
                //console.log(cd.key, opt);
                n[cd.db] = opt;
                setFieldValue(cd.key, opt);
              } catch (e) {
                console.log(e);
              }
            } else if (cd.type == ADT.FILE) {
              // Store existing file URL
              if (x.data[cd.key]) {
                // Special handling for vendor payment multi-file
                if (apiUrl === "/admin/vendorpayment" && cd.key === "fileUrl") {
                  const fileData = x.data[cd.key];
                  if (fileData && fileData.includes("|")) {
                    const [invoiceUrl, airwaybillsStr] = fileData.split("|");
                    setExistingVendorInvoice(invoiceUrl || "");
                    setExistingVendorAirwaybills(airwaybillsStr ? airwaybillsStr.split(",").filter(Boolean) : []);
                  } else {
                    setExistingVendorInvoice(fileData || "");
                    setExistingVendorAirwaybills([]);
                  }
                } else {
                  setExistingFileUrls((prev) => ({
                    ...prev,
                    [cd.key]: x.data[cd.key]
                  }));
                }
                setFieldValue(cd.key, x.data[cd.key]);
              }
            } else {
              let dateAry = ["paymentDate", "invoiceDate", "fromDate", "toDate", "recvDate"];
              if (dateAry.indexOf(cd.key) !== -1) {
                let dtVal = x.data[cd.key];
                if (typeof dtVal === "string" && dtVal[4] === "-") {
                  dtVal = moment(dtVal);
                } else {
                  dtVal = dtVal == "Invalid Date" ? null : moment(dtVal, "DD-MM-YYYY HH:mm:ss");
                }
                vals[cd.key] = dtVal;
                setFieldValue(cd.key, dtVal == null ? "" : dtVal?.format("YYYY-MM-DD"));
              } else {
                setFieldValue(cd.key, x.data[cd.key]);
              }
            }
          });
          setNestedSelectedOptions(n);
        }
      });
    } else {
      let proms = [];

      let nestedApiData = {};
      columnData.forEach((cd) => {
        if (cd.type == ADT.DB) {
          proms.push(
            new Promise((res, rej) => {
              ListCommanWay(`/admin/${cd.db}/list`, {}, { paginate: 1000 })
                .then((x) => {
                  let data: OptionType[] = [];
                  if (x.status == "SUCCESS") {
                    data = x.data.data.map((d) => {
                      return { value: d.id, label: d[cd.dd_label] };
                    });
                    nestedApiData[cd.db] = data;
                  }
                  res(1);
                })
                .catch((c) => {
                  res(0);
                });
            })
          );
        }
      });

      Promise.all(proms).then((x) => {
        console.log("nestedApiData", nestedApiData, nestedSelectedOptions);
        setNestedTables(nestedApiData);
      });
    }
  }, [dataMode]);

  const {
    mutate,
    isLoading: isloading,
  }: {
    mutate: UseMutateFunction<AxiosResponse<any, any>, unknown, any, unknown>;
    isLoading: any;
  } = useMutation(dataMode == "Add" ? CreateCommanWay : UpdateCommanWay, {
    onSuccess: (data: any) => {
      if (data.status == "SUCCESS") {
        UseToast("Data Added Successfully", "success");
        successNavigate();
      } else {
        UseToast(data.message, "error");
      }
    },
    onError: (data: any) => {
      typeof data === "string" ? UseToast(data, "error") : UseToast(data.message, "error");
    },
  });
  // File upload mutation
  const { mutate: uploadFileMutate, isLoading: isUploadingFile } = useMutation(
    ({ file, folderName }: { file: File; folderName: string }) => {
      const formData = new FormData();
      formData.append("files", file);
      formData.append("folderName", folderName);
      return PostFormData("/admin/upload-to-azure", formData);
    },
    {
      onError: () => {
        UseToast("Failed to upload file", "error");
      },
    }
  );

  const { handleSubmit, handleBlur, values, handleChange, errors, setValues, setErrors, touched, dirty, setFieldValue } = useFormik({
    validationSchema: validationSchema,
    initialValues: initialValues,
    onSubmit: async (values, { resetForm }) => {
      let finalValues = { ...values };
      if (dataMode != "Add") {
        columnData.forEach((cd) => {
          if (cd.edit === false) {
            delete finalValues[cd.key];
          }
        });
      }
      if (finalValues.role_name && finalValues.role_name.value) {
        finalValues.role_name = finalValues.role_name.value;
      }
      if (finalValues.subbranch && finalValues.subbranch.value) {
        finalValues.subbranch = finalValues.subbranch.value;
      }
      if (!finalValues.subbranch) {
        delete finalValues.subbranch;
      }
      if (finalValues.isThirdParty && finalValues.isThirdParty.value !== undefined) {
        finalValues.isThirdParty = finalValues.isThirdParty.value;
      }

      if (apiUrl == "/admin/vendorpayment") {
        finalValues.basicAmount = Number(finalValues.basicAmount || 0);
        finalValues.cgst = Number(finalValues.cgst || 0);
        finalValues.sgst = Number(finalValues.sgst || 0);
        finalValues.igst = Number(finalValues.igst || 0);
        finalValues.finalAmount = Number(finalValues.finalAmount || 0);
        finalValues.tds = Number(finalValues.tds || 0);
        finalValues.status = finalValues?.status?.value || finalValues?.status;
        finalValues.vendor = finalValues?.vendor?.value || finalValues?.vendor;
        finalValues.city = finalValues?.city?.value || finalValues?.city;
        
        const pt = finalValues?.payType;
        let extractedValue = typeof pt === 'object' && pt !== null ? pt.value : pt;
        finalValues.payType = extractedValue || undefined;
      }
      //console.log(apiUrl);
      if (apiUrl == "/admin/customer") {
        finalValues.isActive = finalValues?.isActive?.value;
        finalValues.IsSpecialRate = finalValues?.IsSpecialRate?.value;
        finalValues.IsSpecialfuel = finalValues?.IsSpecialfuel?.value;

        if(finalValues?.negative_balance !== false){
          finalValues.negative_balance = finalValues?.negative_balance || finalValues?.negative_balance?.value;
        }
        console.log("finalValues", finalValues.negative_balance);
        delete finalValues.updatedBy;
        finalValues.agreementEnd =
          finalValues.agreementEnd == "Invalid Date" || !finalValues?.agreementEnd ? null : finalValues?.agreementEnd;
        finalValues.agreementStart =
          finalValues?.agreementStart == "Invalid Date" || !finalValues?.agreementStart ? null : finalValues?.agreementStart;
        finalValues.type = "CREDIT";
      }

      // Handle file uploads before submitting the form
      const fileColumns = columnData.filter(cd => cd.type === ADT.FILE);
      const hasFilesToUpload = fileColumns.some(fc => selectedFiles[fc.key]);
      
      // Special check for vendor payment multi-file - check if modified (deleted or added)
      const isVendorPaymentFileField = apiUrl === "/admin/vendorpayment" && 
        columnData.some(cd => cd.type === ADT.FILE && cd.key === "fileUrl");
      const hasVendorPaymentFiles = isVendorPaymentFileField && 
        (vendorInvoiceFile || vendorAirwaybillFiles.length > 0 || vendorFilesModified);

      if (hasFilesToUpload || hasVendorPaymentFiles) {
        try {
          // Special handling for vendor payment multi-file upload
          if (isVendorPaymentFileField && hasVendorPaymentFiles) {
            let invoiceUrl = existingVendorInvoice;
            let airwaybillUrls = [...existingVendorAirwaybills];
            
            // Upload invoice file if new one selected
            if (vendorInvoiceFile) {
              const formData = new FormData();
              formData.append("files", vendorInvoiceFile);
              formData.append("folderName", "vendor_invoices");
              
              const response = await PostFormData("/admin/upload-to-azure", formData);
              
              if (response.status === "SUCCESS") {
                invoiceUrl = response?.data?.uploadSuccess[0]?.path || "";
              } else {
                throw new Error("Failed to upload invoice file");
              }
            }
            
            // Upload airwaybill files if new ones selected
            if (vendorAirwaybillFiles.length > 0) {
              const uploadPromises = vendorAirwaybillFiles.map(async (file) => {
                const formData = new FormData();
                formData.append("files", file);
                formData.append("folderName", "vendor_airwaybills");
                
                const response = await PostFormData("/admin/upload-to-azure", formData);
                
                if (response.status === "SUCCESS") {
                  return response?.data?.uploadSuccess[0]?.path || "";
                } else {
                  throw new Error("Failed to upload airwaybill file");
                }
              });
              
              const newAirwaybillUrls = await Promise.all(uploadPromises);
              airwaybillUrls = [...airwaybillUrls, ...newAirwaybillUrls.filter(Boolean)];
            }
            
            // Format: invoiceUrl|ab1,ab2,ab3
            const formattedFileUrl = invoiceUrl + (airwaybillUrls.length > 0 ? "|" + airwaybillUrls.join(",") : "");
            finalValues.fileUrl = formattedFileUrl;
          }
          
          // Upload all other files (non-vendor-payment)
          const uploadPromises = fileColumns.map(async (fc) => {
            if (selectedFiles[fc.key] && !(apiUrl === "/admin/vendorpayment" && fc.key === "fileUrl")) {
              const formData = new FormData();
              formData.append("files", selectedFiles[fc.key]);
              formData.append("folderName", fc.folderName || "uploads");
              
              const response = await PostFormData("/admin/upload-to-azure", formData);
              
              if (response.status === "SUCCESS") {
                const fileUrl = response?.data?.uploadSuccess[0]?.path;
                if (fileUrl) {
                  finalValues[fc.key] = fileUrl;
                }
              } else {
                throw new Error(`Failed to upload ${fc.label}`);
              }
            }
            return true;
          });

          await Promise.all(uploadPromises);
          
          // After successful upload, proceed with create/update
          mutate({
            url: apiUrl + (dataMode == "Add" ? "/create" : "/update/" + params?.id),
            data:
              apiUrl.indexOf("branch") !== -1 && values["pincode"] !== undefined
                ? { ...values, pincode: Number(values["pincode"]) }
                : finalValues,
          });
        } catch (error) {
          UseToast(error.message || "Failed to upload files", "error");
        }
      } else {
        // No files to upload, proceed directly
        mutate({
          url: apiUrl + (dataMode == "Add" ? "/create" : "/update/" + params?.id),
          data:
            apiUrl.indexOf("branch") !== -1 && values["pincode"] !== undefined
              ? { ...values, pincode: Number(values["pincode"]) }
              : finalValues,
        });
      }
    },
  });

  const successNavigate = useCallback(
    debounce(() => navigate(successNav), 200),
    []
  );

  const onCancel = () => {
    navigate(cancelNav);
  };

  return (
    <div className="p-5">
      {(isloading || isUploadingFile) && <Spinner />}
      <div className="text-right">
        {dataMode == "View" ? (
          <button
            onClick={() => {
              setDataMode("Edit");
            }}
            type="submit"
            className="px-4 py-2 text-sm rounded-md font-semibold bg-warning border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700"
          >
            Edit Details
          </button>
        ) : (
          <button
            onClick={() => {
              setDataMode("View");
            }}
            type="submit"
            className="px-4 py-2 text-sm rounded-md font-semibold bg-warning border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700"
          >
            Cancel Edit
          </button>
        )}
      </div>
      <form
        onSubmit={(e) => {
          console.log(e);
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        <div className={`grid grid-cols-${apiUrl == "/admin/vendorpayment" || apiUrl == "/admin/customer" ? "4" : "3"} gap-4`}>
          {columnData.map((c) => {
            if (c.add === true) {
              let isDisabled = true;
              if ((dataMode == "Add" && c.add) || (dataMode == "Edit" && c.edit)) {
                isDisabled = false;
              }
              switch (c.type) {
                case ADT.NUMBER:
                  return (
                    <div key={"general_" + c.key}>
                      <label htmlFor={"general_" + c.key} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {c.label}
                      </label>
                      <input
                        type="number"
                        id={"general_" + c.key}
                        className={`${
                          isDisabled ? "bg-gray-300" : ""
                        } bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                        placeholder={c.label}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        name={c.key}
                        value={values[c.key]}
                        disabled={isDisabled}
                      />

                      <p className="mt-21 text-sm text-red-600">{touched[c.key] && Boolean(errors[c.key]) ? errors[c.key] + "" : ""}</p>
                    </div>
                  );
                  break;
                case ADT.TEXT:
                  return (
                    <div key={"general_" + c.key}>
                      <label htmlFor={"general_" + c.key} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {c.label}
                      </label>
                      <input
                        type="text"
                        id={"general_" + c.key}
                        className={`${
                          isDisabled ? "bg-gray-300" : ""
                        } bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                        placeholder={c.label}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        name={c.key}
                        value={values[c.key]}
                        disabled={isDisabled}
                      />

                      <p className="mt-21 text-sm text-red-600">{touched[c.key] && Boolean(errors[c.key]) ? errors[c.key] + "" : ""}</p>
                    </div>
                  );
                  break;
                case ADT.DB:
                  return (
                    <div key={"general_" + c.key}>
                      <label htmlFor={"general_" + c.key} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {c.label}
                      </label>

                      <Select
                        key={"select-" + (nestedTables[c.db] ? nestedTables[c.db].length : "undef")}
                        className={`${
                          isDisabled ? "bg-gray-300" : ""
                        } bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block w-full`}
                        id={"general_" + c.key}
                        options={nestedTables[c.db] ? nestedTables[c.db] : defaultOptions}
                        onChange={(el) => {
                          console.log(c.key, el.value, el);
                          setFieldValue(c.key, el.value);

                          let n = { ...nestedSelectedOptions };
                          n[c.db] = el;
                          setNestedSelectedOptions(n);
                        }}
                        isDisabled={isDisabled}
                        name={c.key}
                        value={nestedSelectedOptions[c.db]}
                      />

                      <p className="mt-21 text-sm text-red-600">
                        {touched[c.key] && Boolean(errors[c.key]) ? errors[c.key] + "" + "" : ""}
                      </p>
                    </div>
                  );
                  break;
                case ADT.BOOLEAN:
                  return (
                    <div key={"general_" + c.key}>
                      <label htmlFor={"general_" + c.key} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {c.label}
                      </label>

                      <Select
                        className={`${
                          isDisabled ? "bg-gray-300" : ""
                        } bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block w-full`}
                        id={"general_" + c.key}
                        options={[
                          { value: true, label: "Yes" },
                          { value: false, label: "No" },
                        ]}
                        onChange={(el) => {
                          setFieldValue(c.key, el.value);

                          let n = { ...nestedSelectedOptions };
                          n[c.key] = el;
                          setNestedSelectedOptions(n);
                        }}
                        name={c.key}
                        value={nestedSelectedOptions[c.key]}
                      />

                      <p className="mt-21 text-sm text-red-600">{touched[c.key] && Boolean(errors[c.key]) ? errors[c.key] + "" : ""}</p>
                    </div>
                  );
                  break;
                case ADT.TEXTAREA:
                  break;
                case ADT.SELECT:
                  return (
                    <div key={"general_" + c.key}>
                      <label htmlFor={"general_" + c.key} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {c.label}
                      </label>

                      <Select
                        className={`${
                          isDisabled ? "bg-gray-300" : ""
                        } bg-gray-50 border-gray-300 text-gray-900 text-sm rounded-lg block w-full`}
                        id={"general_" + c.key}
                        options={c.options}
                        onChange={(el) => {
                          console.log(el);
                          setFieldValue(c.key, el.value);
                          let n = { ...nestedSelectedOptions };
                          n[c.key] = el;
                          setNestedSelectedOptions(n);
                        }}
                        name={c.key}
                        value={nestedSelectedOptions[c.key]}
                      />

                      <p className="mt-21 text-sm text-red-600">{touched[c.key] && Boolean(errors[c.key]) ? errors[c.key] + "" : ""}</p>
                    </div>
                  );
                  break;
                case ADT.DATE:
                  return (
                    <div key={"general_" + c.key}>
                      <label htmlFor={"general_" + c.key} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {c.label}
                      </label>
                      <input
                        type="date"
                        id={"general_" + c.key}
                        className={`${
                          isDisabled ? "bg-gray-300" : ""
                        } bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5`}
                        placeholder={c.label}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        name={c.key}
                        value={values[c.key]}
                        disabled={isDisabled}
                      />

                      <p className="mt-21 text-sm text-red-600">{touched[c.key] && Boolean(errors[c.key]) ? errors[c.key] + "" : ""}</p>
                    </div>
                  );
                  break;
                case ADT.DATETIME:
                  break;
                case ADT.FILE:
                  // Special handling for vendor payment multi-file upload
                  if (apiUrl === "/admin/vendorpayment" && c.key === "fileUrl") {
                    return (
                      <div key={"general_" + c.key} className="col-span-2">
                        <div className="grid grid-cols-2 gap-4">
                          {/* Invoice File Upload */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-sm font-medium text-gray-900 dark:text-white">
                                Invoice File
                              </label>
                              {!isDisabled && vendorInvoiceFile && (
                                <button
                                  type="button"
                                  onClick={handleExaCheck}
                                  disabled={invoiceAiLoading}
                                  className={`px-3 py-1 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700 ${invoiceAiLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  {invoiceAiLoading ? 'Processing...' : 'Exa Check'}
                                </button>
                              )}
                            </div>
                            
                            {existingVendorInvoice && (
                              <div className="mb-2 p-2 bg-gray-100 rounded-lg flex items-center justify-between">
                                <a 
                                  href={existingVendorInvoice} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline text-sm truncate flex-1"
                                >
                                  {existingVendorInvoice.split('/').pop()}
                                </a>
                                {!isDisabled && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setExistingVendorInvoice("");
                                      setVendorFilesModified(true); // Mark as modified
                                    }}
                                    className="ml-2 px-2 py-1 text-xs text-white bg-red-500 rounded hover:bg-red-600"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            )}
                            
                            {!isDisabled && (
                              <>
                                <input
                                  type="file"
                                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      setVendorInvoiceFile(file);
                                      setExistingVendorInvoice("");
                                      setVendorFilesModified(true); // Mark as modified
                                    }
                                  }}
                                  accept=".pdf,.jpg,.jpeg,.png"
                                />
                                {vendorInvoiceFile && (
                                  <p className="mt-1 text-sm text-green-600">
                                    Selected: {vendorInvoiceFile.name}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                          
                          {/* Airwaybill Files Upload (Multiple) */}
                          <div>
                            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                              Airwaybill Files (Multiple)
                            </label>
                            
                            {existingVendorAirwaybills.length > 0 && (
                              <div className="mb-2 space-y-1">
                                {existingVendorAirwaybills.map((url, idx) => (
                                  <div key={idx} className="p-2 bg-gray-100 rounded-lg flex items-center justify-between">
                                    <a 
                                      href={url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline text-sm truncate flex-1"
                                    >
                                      {url.split('/').pop()}
                                    </a>
                                    {!isDisabled && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setExistingVendorAirwaybills(prev => prev.filter((_, i) => i !== idx));
                                          setVendorFilesModified(true); // Mark as modified
                                        }}
                                        className="ml-2 px-2 py-1 text-xs text-white bg-red-500 rounded hover:bg-red-600"
                                      >
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {!isDisabled && (
                              <>
                                <input
                                  type="file"
                                  multiple
                                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                  onChange={(e) => {
                                    const files = Array.from(e.target.files || []);
                                    if (files.length > 0) {
                                      setVendorAirwaybillFiles(files);
                                      setVendorFilesModified(true); // Mark as modified
                                    }
                                  }}
                                  accept=".pdf,.jpg,.jpeg,.png"
                                />
                                {vendorAirwaybillFiles.length > 0 && (
                                  <p className="mt-1 text-sm text-green-600">
                                    Selected: {vendorAirwaybillFiles.length} file(s)
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  // Default file upload for other cases
                  return (
                    <div key={"general_" + c.key}>
                      <label htmlFor={"general_" + c.key} className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                        {c.label}
                      </label>
                      
                      {/* Show existing file in view/edit mode */}
                      {existingFileUrls[c.key] && (
                        <div className="mb-2 p-2 bg-gray-100 rounded-lg flex items-center justify-between">
                          <a 
                            href={existingFileUrls[c.key]} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm truncate flex-1"
                          >
                            {existingFileUrls[c.key].split('/').pop() || 'View File'}
                          </a>
                          {!isDisabled && (
                            <button
                              type="button"
                              onClick={() => {
                                setExistingFileUrls((prev) => {
                                  const updated = { ...prev };
                                  delete updated[c.key];
                                  return updated;
                                });
                                setFieldValue(c.key, null);
                              }}
                              className="ml-2 px-2 py-1 text-xs text-white bg-red-500 rounded hover:bg-red-600"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                      
                      {/* File input */}
                      {!isDisabled && (
                        <>
                          <input
                            type="file"
                            id={"general_" + c.key}
                            ref={(el) => (fileInputRefs.current[c.key] = el)}
                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setSelectedFiles((prev) => ({
                                  ...prev,
                                  [c.key]: file
                                }));
                                // Clear existing file URL when new file is selected
                                if (existingFileUrls[c.key]) {
                                  setExistingFileUrls((prev) => {
                                    const updated = { ...prev };
                                    delete updated[c.key];
                                    return updated;
                                  });
                                }
                              }
                            }}
                            disabled={isDisabled}
                            accept={c.accept || "*/*"}
                          />
                          {selectedFiles[c.key] && (
                            <p className="mt-1 text-sm text-green-600">
                              Selected: {selectedFiles[c.key].name}
                            </p>
                          )}
                        </>
                      )}
                      
                      <p className="mt-1 text-sm text-red-600">
                        {touched[c.key] && Boolean(errors[c.key]) ? errors[c.key] + "" : ""}
                      </p>
                    </div>
                  );
                  break;
                default:
                  return <></>;
                  break;
              }
            }
          })}
        </div>
        {dataMode != "View" && (
          <div className="inline-flex rounded-md overflow-hidden shadow-sm mb-1 mt-4" role="group">
            <button
              type="submit"
              onClick={() => {
                console.log(values, errors);
              }}
              disabled={isloading || isUploadingFile}
              className={`px-4 py-2 text-sm font-medium text-white bg-green-500 border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 ${(isloading || isUploadingFile) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isloading || isUploadingFile ? 'Processing...' : (dataMode == "Edit" ? "Save" : "Add")}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700"
            >
              Back to List
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export { GeneralAddForm };
